import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, Validators, FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from 'src/app/services/app/auth.service';
import { CompanyService } from 'src/app/services/app/company.service';
import { LogService } from 'src/app/services/common/log.service';
import { isPlatform } from '@ionic/angular';
import { SubscriberLoginResp } from 'src/app/models/common/SubscriberLoginResp';
import { CookieService } from 'ngx-cookie-service';
import { Preferences } from '@capacitor/preferences';
import { SignalrService } from 'src/app/services/app/signalr.service';
import { AppService } from 'src/app/services/app/app.service';
import { InputText } from 'primeng/inputtext';
import { Password } from 'primeng/password';
import { SelectModule } from 'primeng/select';
import { NgStyle } from '@angular/common';
import { environment } from 'src/environments/environment';
import { NgxCaptchaModule } from 'ngx-captcha';

interface FinancialYear {
  value: string;
  viewValue: string;
}

@Component({
    standalone: true,
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.css'],
    imports: [FormsModule, ReactiveFormsModule, InputText, Password, SelectModule, NgStyle, NgxCaptchaModule]
})
export class LoginComponent implements OnInit {
  private readonly cdr = inject(ChangeDetectorRef);

  ErrorMessage: string;
  loginForm: FormGroup;
  hide = true;
  selectedValue: string;
  financialyears: FinancialYear[] = [];
  token: string | undefined;
  companyID = "";
  companyName = "";
  deleteMode = "";
  financialYear = "";
  isDelete = false;
  displayConfirmDelete = 'none';
  subscriberId = null;
  subscriberResp: SubscriberLoginResp;
  subscriberString: string;
  isLoading = false;
  rememberMe = false;
  captchaSiteKey = environment.recaptcha.siteKey;
  captchaTheme: 'light' | 'dark' = 'light';
  captchaSize: 'compact' | 'normal' = 'normal';
  captchaLang = 'en';
  captchaType: 'image' | 'audio';
  reCaptchaVerification: string | null = null;

  constructor(
    private formBuilder: FormBuilder,
    private authService: AuthService,
    private companyService: CompanyService,
    private router: Router,
    private route: ActivatedRoute,
    private logService: LogService,
    private cookieService: CookieService,
    private signalRService: SignalrService,
    private appService: AppService
  ) {}

  async ngOnInit() {
    this.route.queryParamMap.subscribe(params => {
      if (params.has('deleteMode') && params.has('companyID') && params.has('financialYear')) {
        this.isDelete = true;
        this.deleteMode = params.get('deleteMode');
        this.companyID = params.get('companyID');
        this.financialYear = params.get('financialYear');
      } else if (params.has('deleteMode') && params.has('companyID')) {
        this.isDelete = true;
        this.deleteMode = params.get('deleteMode');
        this.companyID = params.get('companyID');
        this.financialYear = params.get('financialYear');
      } else if (params.has('companyID') && params.has('companyName')) {
        this.isDelete = false;
        this.companyID = params.get('companyID');
        this.companyName = params.get('companyName');
      }
    });

    if(isPlatform('capacitor')) {
      await Preferences.set({ key: 'CompanyID', value: this.companyID });
      if(this.companyName !== '' && this.companyName !== null) {
        await Preferences.set({ key: 'CompanyName', value: this.companyName });
      }
    } else {
      sessionStorage.setItem('CompanyID', this.companyID);
      if(this.companyName !== '' && this.companyName !== null) {
        sessionStorage.setItem('CompanyName', this.companyName);
      }
    }

    this.initForm();
    this.getSubscriberDetails();
    this.GetFinancialYear();
  }

  initForm() {
    this.loginForm = this.formBuilder.group({
      Username: ['', [Validators.required]],
      Password: ['', [Validators.required]],
      recaptcha: [null]
    });

    this.toggleFinancialYearControl();
  }

  toggleFinancialYearControl(): void {
    if (this.deleteMode === 'financialYear' || !this.isDelete) {
      // Add financialyear control with required validator
      this.loginForm.addControl('FinancialYear', this.formBuilder.control('', Validators.required));
    } else {
      // Remove financialyear control
      this.loginForm.removeControl('FinancialYear');
    }
  }

  async getSubscriberDetails() {
    if (isPlatform('capacitor')) {
      this.subscriberResp = await this.authService.getPreferenceSubscriberDetails();
    } else {
      this.subscriberString = sessionStorage.getItem('subscriber');
      if (!this.subscriberString) {
        this.subscriberString = this.cookieService.get('subscriber');
      }
      this.subscriberResp = this.subscriberString ? JSON.parse(this.subscriberString) : null;
    }

    this.subscriberId = this.subscriberResp?.subscriberID;
  }

  async GetFinancialYear() {
    try {
      const companyId = isPlatform('capacitor') ? (await Preferences.get({ key: 'CompanyID' })).value : sessionStorage.getItem("CompanyID");
      let formGroup = new FormGroup({
        CompanyID: new FormControl(companyId)
      });

      const result = await this.authService.GetFinancialYear(formGroup.value).toPromise();
      const years = result.financialYear.split(",");
      this.financialyears = years.map(year => ({
        value: year,
        viewValue: year.split("/")[2]
      }));
    } catch (ex) {
      this.logService.InsertExceptionLog(ex, "Login", "GetFinancialYear");
      console.log("Error in GetFinancialYear: ", ex);
    }
  }

  private setLoading(value: boolean): void {
    this.isLoading = value;
    this.cdr.detectChanges();
  }

  async Userlogin() {
    try {
      if (this.loginForm.valid) {
        this.setLoading(true);
        // Clear stale company auth before issuing a fresh login request.
        sessionStorage.removeItem('ctoken');
        sessionStorage.removeItem('companyUser');
        sessionStorage.removeItem('RoleID');
        sessionStorage.removeItem('daysLeft');

        const companyId = isPlatform('capacitor') ? (await Preferences.get({ key: 'CompanyID' })).value : sessionStorage.getItem("CompanyID");
        this.financialYear = this.loginForm.value.FinancialYear;
        const { recaptcha, ...formValues } = this.loginForm.value;
        const loginData = {
          ...formValues,
          CompanyID: companyId
        };

        const result = await this.authService.companyLogin(loginData).toPromise();
        if (result == 1) {
          if(isPlatform('capacitor')) {
            await Preferences.set({ key: 'FinancialYear', value: loginData.FinancialYear });
          } else {
            sessionStorage.setItem('FinancialYear', loginData.FinancialYear);
          }

          const daysLeft = sessionStorage.getItem('daysLeft');
          if (daysLeft == '-1') {
            this.ErrorMessage = "Subscription has Ended. Please renew your package";
            this.setLoading(false);
          } else {
            const companyToken = sessionStorage.getItem('ctoken');
            if (companyToken && companyToken !== 'undefined' && companyToken !== 'null') {
              this.appService.loadInitialData(+companyId, this.financialYear).subscribe({
                next: (success) => {
                  if (success) {
                    this.signalRService.startConnection(companyId);
                    if (this.isDelete) {
                      this.openConfirm();
                    } else {
                      this.router.navigate(['admin']);
                    }
                  }
                  this.setLoading(false);
                },
                error: () => {
                  this.setLoading(false);
                }
              });
            } else {
              this.ErrorMessage = 'Login succeeded but company token was not issued.';
              this.setLoading(false);
            }
          }
        } else {
          this.setLoading(false);
          this.ErrorMessage = "Invalid Username or Password.";
        }
      }
    } catch (ex) {
      this.setLoading(false);
      this.logService.InsertExceptionLog(ex, "Login", "Userlogin");
      console.log(ex);
    }
  }
  

  getErrorMessage() {
    const u = this.loginForm?.get('Username');
    if (!u) {
      return '';
    }
    if (u.hasError('required')) {
      return 'You must enter a value';
    }
    return u.hasError('email') ? 'Not a valid email' : '';
  }

  // GetFinancialYear() {
  //   try {
  //     this.formGroup = new FormGroup({
  //       CompanyID: new FormControl(sessionStorage.getItem("CompanyID"))
  //     });

  //     if (this.formGroup.valid) {
  //       this.authService.GetFinancialYear(this.formGroup.value).subscribe(
  //         (result) => {
  //           const years = result.financialYear.split(",");
  //           for (var i = 0; i < years.length; i++) {
  //             var year: FinancialYear = {
  //               value: years[i],
  //               viewValue: years[i].split("/")[2]
  //             };

  //             this.financialyears.push(year);
  //           }
  //         }, (err: Error) => {
  //           console.log(err.message);
  //         })
  //     }
  //   }
  //   catch (ex) {
  //     this.logService.InsertExceptionLog(ex,"Login","GetFinancialYear");
  //     console.log(ex);
  //   }
  // }

  async logout(): Promise<void> {
    try {
      await this.authService.signOut();
    }
    catch (ex) {
      this.logService.InsertExceptionLog(ex,"Login","logout");
      console.log(ex);
    }
  }

  selectCompany() {
    try {
      this.router.navigate(
        ['view-company'],
        { queryParams: { subscriberID: this.subscriberId } }
      )
    } catch (ex) {
      console.log(ex);
    }
  }

  deleteCompany() {
    if (this.deleteMode === "company") {
      this.deleteCompanyById(this.companyID);
    } else if (this.deleteMode === "financialYear") {
      if (this.financialyears.length === 1) {
        this.deleteCompanyById(this.companyID);
      } else {
        this.deleteCompanyByFY(this.companyID, this.financialYear);
      }
    }
  }

  deleteCompanyById(companyId: string) {
    let formGroup = new FormGroup({
      CompanyID: new FormControl(companyId)
    });
    try {
      this.companyService.DeleteCompany(formGroup.value).subscribe(
        () => {
          this.clearSession();
          this.selectCompany();
        }, (err: Error) => {
          this.clearSession();
          console.log(err.message);
        });
    }
    catch (ex) {
      this.clearSession();
      console.log(ex);
    }
  }

  deleteCompanyByFY(companyId: string, financialYear: string) {
    let formGroup = new FormGroup({
      CompanyID: new FormControl(companyId),
      FinancialYear: new FormControl(financialYear)
    });

    try {
      this.companyService.DeleteCompanyByFY(formGroup.value).subscribe(
        () => {
          let fyStartDate = [];
          for (let i = 0; i < this.financialyears.length; i++) {
            if (this.financialyears[i].value === financialYear) {
              this.financialyears.splice(i, 1);
            } else {
              fyStartDate.push(this.financialyears[i].value);
            }
          }

          this.companyService.GetCompanyByID().subscribe(
            (result) => {
              result.fyStartDate = fyStartDate.join(",");
              this.companyService.UpdateCompany(result).subscribe(
                (result) => {
                  if (result === 1) {
                    this.clearSession();
                    this.selectCompany();
                  }
                }, (err: Error) => {
                  this.clearSession();
                  console.log(err.message);
                });
            }, (err: Error) => {
              this.clearSession();
              console.log(err.message);
            });
        }, (err: Error) => {
          this.clearSession();
          console.log(err.message);
        });
    }
    catch (ex) {
      this.clearSession();
      this.logService.InsertExceptionLog(ex,"Login","selectCompany");
      console.log(ex);
    }
  }

  clearSession() {
    if (this.deleteMode === "company") {
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('companyID');
      sessionStorage.removeItem('CompanyName');
      sessionStorage.removeItem('RoleID');
      sessionStorage.removeItem('UsernameEmail');
    } else if (this.deleteMode === "financialYear") {
      sessionStorage.removeItem('token');
    }
  }

  confirmDelete() {
    this.deleteCompany();
  }

  closeConfirm() {
    this.displayConfirmDelete = 'none';
    this.clearSession();
  }

  openConfirm() {
    this.displayConfirmDelete = 'block';
  }

  redirectToForgotPassword() {
    this.router.navigate(
      ['forgot-password']
    );
  }

  redirectToSignup() {
    this.router.navigate(['subscriber-signup']);
  }

  handleCaptchaSuccess(data: string) {
    this.reCaptchaVerification = data;
  }

}
